import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { FloatLabelModule } from 'primeng/floatlabel';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { MultiSelectModule } from 'primeng/multiselect';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ZoomParticipantService } from 'src/app/services/zoomparticipants.service';
import { ZoomParticipant } from 'src/app/models/zoomparticipants.model';
import { ZoomMeeting } from 'src/app/models/zoommeeting.model';

@Component({
  selector: 'app-presence',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FloatLabelModule,
    DropdownModule,
    ButtonModule,
    MultiSelectModule,
    RadioButtonModule,
  ],
  templateUrl: './presence.component.html',
  styleUrls: ['./presence.component.scss'],
})
export class PresenceDialogComponent implements OnInit, OnDestroy {
  /* -------------------- Daten -------------------- */
  availableMeetings: ZoomMeeting[] = [];
  allParticipants: ZoomParticipant[] = [];
  availableParticipants: ZoomParticipant[] = [];

  selectedKurs: string[] = [];
  selectedTeilnehmer: ZoomParticipant[] = [];
  selectedMonat: number | null = null;

  auswahlModus: 'kurs' | 'teilnehmer' = 'kurs';
  loadingExcel = false;

  private readonly destroy$ = new Subject<void>();
  private readonly EXPIRED_COURSE_IDS = new Set([
    '86889666186', '87273088949', '81478276614', '87036554365',
    '86006762360', '89474702329', '87997163502', '87015534106',
    '83179091385', '84735183130', '82354880455', '87929708799',
    '87036554365', '89582387786','89847129983',
  ]);

  readonly monate = Array.from({ length: 12 }, (_, i) => ({
    label: new Date(0, i).toLocaleString('de-DE', { month: 'long' }),
    value: i + 1,
  }));

  private readonly zoomService = inject(ZoomParticipantService);
  private readonly ref = inject(DynamicDialogRef);

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeDialog(): void {
    this.ref.close();
  }

  private computeYear(month: number): number {
    const now = new Date();
    return month > now.getMonth() + 1 ? now.getFullYear() - 1 : now.getFullYear();
  }

  private loadData(): void {
    forkJoin({
      meetings: this.zoomService.getMeetings(),
      participants: this.zoomService.getAllParticipants(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ meetings, participants }) => {
          this.availableMeetings = meetings.filter(
            (m) => !this.EXPIRED_COURSE_IDS.has(m.meetingId)
          );
          this.allParticipants = participants;

          if (this.auswahlModus === 'teilnehmer') {
            this.setTeilnehmerMode();
          }
        },
        error: (err) => console.error('Datenladen fehlgeschlagen', err),
      });
  }

  onCourseSelected(meetingIds: string[]): void {
    if (!meetingIds?.length) {
      this.availableParticipants = [];
      this.selectedTeilnehmer = [];
      return;
    }

    const filtered = this.allParticipants.filter((p) =>
      meetingIds.includes(p.meetingId)
    );

    const unique = [
      ...new Map(filtered.map((p) => [`${p.name}|${p.meetingId}`, p])).values(),
    ];

    this.availableParticipants = unique;
    this.selectedTeilnehmer = unique; // Auto-select all
  }

  onParticipantSelected(participants: ZoomParticipant[]): void {
    this.selectedTeilnehmer = participants ?? [];
    this.selectedKurs = [...new Set(participants.map((p) => p.meetingId))];
  }

  updateDisabledStates(): void {
    this.selectedKurs = [];
    this.selectedTeilnehmer = [];
    this.availableParticipants = [];

    if (this.auswahlModus === 'teilnehmer') {
      this.setTeilnehmerMode();
    }
  }

  private setTeilnehmerMode(): void {
    const uniqueByName = [
      ...new Map(this.allParticipants.map((p) => [p.name, p])).values(),
    ];
    this.availableParticipants = uniqueByName;
  }

  downloadExcel(): void {
  if (!this.canDownload()) return;

  this.loadingExcel = true;
  
  // Primary ID senden - Backend löst Gruppe auf
  const request = {
    year: this.computeYear(this.selectedMonat!),
    month: this.selectedMonat!,
    courseName: this.auswahlModus === 'kurs' 
      ? this.availableMeetings.find(m => m.meetingId === this.selectedKurs[0])?.topic 
      : 'Mehrere_Teilnehmer',
    participantNames: this.selectedTeilnehmer.map(p => p.name),
    meetingId: this.selectedKurs[0] // ← z.B. "88554610822"
  };

  this.zoomService
  .exportMonthlyExcel(request, this.auswahlModus)
  .subscribe({
    next: (blob) => this.handleDownloadSuccess(blob, request),
    error: (err) => {
      console.error('Download fehlgeschlagen', err);
      this.loadingExcel = false;
    }
  });
}

  private canDownload(): boolean {
    return !!(
      this.selectedMonat &&
      (this.auswahlModus === 'kurs'
        ? this.selectedKurs.length
        : this.selectedTeilnehmer.length)
    );
  }

  private buildRequest() {
    const year = this.computeYear(this.selectedMonat!);
    const month = this.selectedMonat!;
    const participantNames = this.selectedTeilnehmer.map((p) => p.name);
    
    const meetingIds =
      this.auswahlModus === 'kurs'
        ? this.selectedKurs
        : [...new Set(this.selectedTeilnehmer.map((p) => p.meetingId))];

    const courseName =
      this.auswahlModus === 'kurs'
        ? this.availableMeetings.find((m) => m.meetingId === meetingIds[0])?.topic
        : 'Mehrere_Kurse';

    return {
      year,
      month,
      courseName: courseName ?? 'Unbekannt',
      participantNames,
      meetingId: meetingIds[0] ?? '',
    };
  }

  private handleDownloadSuccess(blob: Blob, request: any): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    const baseName =
      this.auswahlModus === 'kurs'
        ? request.courseName
        : request.participantNames.length > 1
        ? `${request.participantNames.length}_Teilnehmer`
        : request.participantNames[0] ?? 'Teilnehmer';

    a.download = `${baseName}_${request.year}_${String(request.month).padStart(2, '0')}.xlsx`
      .replace(/[/\\?%*:|"<>]/g, '_');

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    this.loadingExcel = false;
  }
}